import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { adminCmsAPI } from '../../lib/cmsApi';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Plus,
  Edit2,
  Trash2,
  Loader2,
  Eye,
  EyeOff,
  Image,
  MapPin,
  FolderOpen,
} from 'lucide-react';

const CmsProjects = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [services, setServices] = useState([]);
  const [editingProject, setEditingProject] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mediaList, setMediaList] = useState([]);
  const [showMediaPicker, setShowMediaPicker] = useState(false);

  const categories = [
    'Gestion des eaux',
    'Hydrologie',
    'Infrastructures vertes',
    'Transport',
    'Modélisation',
    'Formation',
    'Autre',
  ];

  const defaultProject = {
    title: '',
    slug: '',
    location: '',
    category: 'Gestion des eaux',
    short_description: '',
    full_description: '',
    image_url: '',
    gallery_images: [],
    client_name: '',
    year: new Date().getFullYear(),
    services_used: [],
    order: 0,
    is_featured: false,
    status: 'publie',
  };

  useEffect(() => {
    if (user?.role !== 'administrateur' && user?.role !== 'employe') {
      navigate('/portail');
      return;
    }
    fetchProjects();
    fetchServices();
    fetchMedia();
  }, [user, navigate]);

  const fetchProjects = async () => {
    try {
      const response = await adminCmsAPI.listProjects();
      setProjects(response.data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des projets');
    } finally {
      setLoading(false);
    }
  };

  const fetchServices = async () => {
    try {
      const response = await adminCmsAPI.listServices();
      setServices(response.data || []);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const fetchMedia = async () => {
    try {
      const response = await adminCmsAPI.listMedia('projects');
      setMediaList(response.data || []);
    } catch (error) {
      console.error('Error fetching media:', error);
    }
  };

  const handleSave = async () => {
    if (!editingProject.title || !editingProject.slug) {
      toast.error('Le titre et le slug sont requis');
      return;
    }

    setSaving(true);
    try {
      if (editingProject.id) {
        await adminCmsAPI.updateProject(editingProject.id, editingProject);
        toast.success('Projet mis à jour');
      } else {
        await adminCmsAPI.createProject(editingProject);
        toast.success('Projet créé');
      }
      setShowDialog(false);
      setEditingProject(null);
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce projet?')) return;
    
    try {
      await adminCmsAPI.deleteProject(id);
      toast.success('Projet supprimé');
      fetchProjects();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleStatus = async (project) => {
    try {
      const newStatus = project.status === 'publie' ? 'brouillon' : 'publie';
      await adminCmsAPI.updateProject(project.id, { status: newStatus });
      toast.success(`Projet ${newStatus === 'publie' ? 'publié' : 'mis en brouillon'}`);
      fetchProjects();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleServicesUsedChange = (serviceTitle) => {
    const current = editingProject.services_used || [];
    if (current.includes(serviceTitle)) {
      setEditingProject({
        ...editingProject,
        services_used: current.filter(s => s !== serviceTitle),
      });
    } else {
      setEditingProject({
        ...editingProject,
        services_used: [...current, serviceTitle],
      });
    }
  };

  const selectImage = (url) => {
    setEditingProject({ ...editingProject, image_url: url });
    setShowMediaPicker(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#F9B438]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="cms-projects">
      {/* Header */}
      <header className="bg-[#1D1D1B] text-white py-6">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/cms">
                <Button variant="ghost" size="icon" className="text-white/70 hover:text-white">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <div>
                <h1 className="font-heading text-2xl font-bold">Projets publics</h1>
                <p className="text-white/60 text-sm">{projects.length} projet(s)</p>
              </div>
            </div>
            <Button 
              onClick={() => {
                setEditingProject({ ...defaultProject, order: projects.length });
                setShowDialog(true);
              }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouveau projet
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project) => (
            <Card key={project.id} className="hover:shadow-md transition-shadow overflow-hidden">
              <div className="aspect-video bg-neutral-100 relative">
                {project.image_url ? (
                  <img 
                    src={project.image_url} 
                    alt={project.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <FolderOpen className="w-16 h-16 text-neutral-300" />
                  </div>
                )}
                <div className="absolute top-2 right-2 flex gap-1">
                  <Badge variant={project.status === 'publie' ? 'default' : 'secondary'}>
                    {project.status === 'publie' ? 'Publié' : 'Brouillon'}
                  </Badge>
                  {project.is_featured && (
                    <Badge className="bg-[#F9B438] text-[#1D1D1B]">Vedette</Badge>
                  )}
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                  <Badge variant="outline" className="bg-black/50 text-white border-white/30 mb-2">
                    {project.category}
                  </Badge>
                  <h3 className="font-semibold text-white">{project.title}</h3>
                </div>
              </div>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-sm text-neutral-500 mb-2">
                  <MapPin className="w-4 h-4" />
                  {project.location}
                  {project.year && <span>• {project.year}</span>}
                </div>
                <p className="text-sm text-neutral-600 line-clamp-2 mb-3">{project.short_description}</p>
                <div className="flex items-center justify-between pt-2 border-t">
                  <span className="text-xs text-neutral-400">
                    {project.client_name || 'Client non spécifié'}
                  </span>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => toggleStatus(project)}
                    >
                      {project.status === 'publie' ? (
                        <Eye className="w-4 h-4 text-green-600" />
                      ) : (
                        <EyeOff className="w-4 h-4 text-neutral-400" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => {
                        setEditingProject(project);
                        setShowDialog(true);
                      }}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => handleDelete(project.id)}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {projects.length === 0 && (
            <Card className="md:col-span-2 lg:col-span-3">
              <CardContent className="p-12 text-center">
                <FolderOpen className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
                <p className="text-neutral-500 mb-4">Aucun projet pour le moment</p>
                <Button 
                  onClick={() => {
                    setEditingProject({ ...defaultProject });
                    setShowDialog(true);
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter le premier projet
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      {/* Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingProject?.id ? 'Modifier le projet' : 'Nouveau projet'}
            </DialogTitle>
          </DialogHeader>
          
          {editingProject && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Image principale</Label>
                <div className="flex gap-2">
                  <Input
                    value={editingProject.image_url || ''}
                    onChange={(e) => setEditingProject({ ...editingProject, image_url: e.target.value })}
                    placeholder="URL de l'image"
                    className="flex-1"
                  />
                  <Button variant="outline" onClick={() => setShowMediaPicker(true)}>
                    <Image className="w-4 h-4 mr-2" />
                    Choisir
                  </Button>
                </div>
                {editingProject.image_url && (
                  <img 
                    src={editingProject.image_url} 
                    alt="Aperçu"
                    className="w-full h-40 object-cover rounded mt-2"
                  />
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Titre *</Label>
                  <Input
                    value={editingProject.title}
                    onChange={(e) => {
                      const title = e.target.value;
                      setEditingProject({
                        ...editingProject,
                        title,
                        slug: editingProject.id ? editingProject.slug : generateSlug(title),
                      });
                    }}
                    placeholder="Bio-rétention urbaine"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Slug *</Label>
                  <Input
                    value={editingProject.slug}
                    onChange={(e) => setEditingProject({ ...editingProject, slug: e.target.value })}
                    placeholder="bioretention-urbaine"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Localisation</Label>
                  <Input
                    value={editingProject.location || ''}
                    onChange={(e) => setEditingProject({ ...editingProject, location: e.target.value })}
                    placeholder="Montréal, QC"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Catégorie</Label>
                  <select
                    className="w-full h-10 px-3 rounded-md border border-input bg-background"
                    value={editingProject.category}
                    onChange={(e) => setEditingProject({ ...editingProject, category: e.target.value })}
                  >
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Client</Label>
                  <Input
                    value={editingProject.client_name || ''}
                    onChange={(e) => setEditingProject({ ...editingProject, client_name: e.target.value })}
                    placeholder="Ville de Montréal"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Année</Label>
                  <Input
                    type="number"
                    value={editingProject.year || ''}
                    onChange={(e) => setEditingProject({ ...editingProject, year: parseInt(e.target.value) || null })}
                    placeholder="2024"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description courte</Label>
                <Textarea
                  value={editingProject.short_description || ''}
                  onChange={(e) => setEditingProject({ ...editingProject, short_description: e.target.value })}
                  placeholder="Résumé du projet..."
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>Description complète</Label>
                <Textarea
                  value={editingProject.full_description || ''}
                  onChange={(e) => setEditingProject({ ...editingProject, full_description: e.target.value })}
                  placeholder="Description détaillée du projet..."
                  rows={4}
                />
              </div>

              <div className="space-y-2">
                <Label>Services utilisés</Label>
                <div className="flex flex-wrap gap-2">
                  {services.map((service) => (
                    <label key={service.id} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editingProject.services_used?.includes(service.title)}
                        onChange={() => handleServicesUsedChange(service.title)}
                        className="rounded"
                      />
                      <span className="text-sm">{service.title}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Statut</Label>
                  <select
                    className="w-full h-10 px-3 rounded-md border border-input bg-background"
                    value={editingProject.status}
                    onChange={(e) => setEditingProject({ ...editingProject, status: e.target.value })}
                  >
                    <option value="publie">Publié</option>
                    <option value="brouillon">Brouillon</option>
                  </select>
                </div>
                <div className="flex items-center gap-4 pt-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editingProject.is_featured}
                      onChange={(e) => setEditingProject({ ...editingProject, is_featured: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm">Mettre en vedette</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingProject?.id ? 'Mettre à jour' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Media Picker Dialog */}
      <Dialog open={showMediaPicker} onOpenChange={setShowMediaPicker}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Choisir une image</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-4 gap-4 py-4 max-h-[60vh] overflow-y-auto">
            {mediaList.filter(m => m.file_type === 'image').map((media) => (
              <div
                key={media.id}
                className="aspect-video bg-neutral-100 rounded overflow-hidden cursor-pointer hover:ring-2 hover:ring-[#F9B438] transition-all"
                onClick={() => selectImage(media.url)}
              >
                <img
                  src={media.url}
                  alt={media.alt_text || media.original_filename}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
            {mediaList.filter(m => m.file_type === 'image').length === 0 && (
              <p className="col-span-4 text-center text-neutral-500 py-8">
                Aucune image disponible. Téléversez d'abord des images dans la médiathèque.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMediaPicker(false)}>
              Annuler
            </Button>
            <Link to="/cms/media">
              <Button>
                <Plus className="w-4 h-4 mr-2" />
                Téléverser une image
              </Button>
            </Link>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CmsProjects;
